import { useState, useEffect, useCallback, useRef } from "react";
import {
  FacebookPublish,
  YoutubePublish,
  SocialPublish,
  MediaModel,
} from "@/database";
import { editedArrayToFields } from "../utils";

interface UsePublishManagementProps {
  currentMediaDetails: MediaModel;
  isPreview: boolean;
}

interface UsePublishManagementReturn {
  youtubePublishes: YoutubePublish[];
  facebookPublishes: FacebookPublish[];
  socialPublishes: SocialPublish[];
  publishOrder: string[];
  addPublish: (
    publish: YoutubePublish | FacebookPublish | SocialPublish,
    platform: string
  ) => void;
  removePublish: (id: string, platform: string) => void;
  setYoutubePublishes: React.Dispatch<React.SetStateAction<YoutubePublish[]>>;
  setFacebookPublishes: React.Dispatch<React.SetStateAction<FacebookPublish[]>>;
  setSocialPublishes: React.Dispatch<React.SetStateAction<SocialPublish[]>>;
  setPublishOrder: React.Dispatch<React.SetStateAction<string[]>>;
}

export const usePublishManagement = ({
  currentMediaDetails,
  isPreview,
}: UsePublishManagementProps): UsePublishManagementReturn => {
  const [youtubePublishes, setYoutubePublishes] = useState<YoutubePublish[]>(
    []
  );
  const [facebookPublishes, setFacebookPublishes] = useState<FacebookPublish[]>(
    []
  );
  const [socialPublishes, setSocialPublishes] = useState<SocialPublish[]>([]);
  const [publishOrder, setPublishOrder] = useState<string[]>([]);
  const prevMediaIdRef = useRef<string | undefined>(currentMediaDetails?.id);

  // Sync publishes with currentMediaDetails.socials
  useEffect(() => {
    if (isPreview) return;

    // When the active media itself changes (e.g. switching bulk-upload tabs),
    // any not-yet-published draft chip belonged to the PREVIOUS media - drop it
    // instead of carrying it forward onto the new media.
    const mediaChanged = prevMediaIdRef.current !== currentMediaDetails?.id;
    prevMediaIdRef.current = currentMediaDetails?.id;

    if (currentMediaDetails?.socials) {
      const prevById = new Map<string, any>();
      if (!mediaChanged) {
        [...youtubePublishes, ...facebookPublishes, ...socialPublishes].forEach(
          (c: any) => prevById.set(c.id, c)
        );
      }
      const mergeEdited = (item: any, prev: any) => ({
        ...(prev?._editedFields || {}),
        ...editedArrayToFields(item?.edited),
      });
      // The collapsed card always shows `seeded`/`base` as-is (fresh from
      // currentMediaDetails.socials) - an in-progress edit never overwrites
      // it here. `_editedValues` (not the display fields) is what's carried
      // forward so BasePublishCard's formik can still restore the typed
      // value when the sheet reopens, without that value ever leaking onto
      // the collapsed card before the real update is confirmed.
      const carryClientMarkers = (item: any, seeded: any) => {
        const prev = prevById.get(seeded.id);
        const _editedFields = mergeEdited(item, prev);
        return {
          ...seeded,
          _editedFields,
          ...(prev?._editedValues ? { _editedValues: prev._editedValues } : {}),
          ...(prev?._cardSaved ? { _cardSaved: true } : {}),
          ...(prev?._dirty ? { _dirty: true } : {}),
          ...(prev?._stagedPayload
            ? { _stagedPayload: prev._stagedPayload }
            : {}),
        };
      };

      // `currentMediaDetails.socials` can contain multiple raw records for the
      // same destination account - not just a failed attempt plus a retry,
      // but now also several genuine republish attempts (full/clip/segment)
      // to the same channel. Every record gets its own rendered card, keyed
      // by the record's own (unique) id - `_channelId` carries the real
      // destination id (channel_id, falling back to platform) separately,
      // for avatar/history/routing lookups that need to know which real
      // channel a card belongs to regardless of which record it is.
      const destinationKey = (item: any): string =>
        item.channel_id || item.platform;

      const ytPublishes: YoutubePublish[] = currentMediaDetails.socials
        .filter((item) => item.job_type === "youtube")
        .map((item) =>
          carryClientMarkers(item, {
            ...item,
            id: item.id,
            _channelId: destinationKey(item),
            published_id: item.id,
            routing_id: item.published_id,
          })
        );

      const fbPublishes: FacebookPublish[] = currentMediaDetails.socials
        .filter((item) => item.job_type === "facebook")
        .map((item) =>
          carryClientMarkers(item, {
            ...item,
            id: item.id,
            _channelId: destinationKey(item),
            published_id: item.id,
            routing_id: item.published_id,
            channel_details:
              "channel_details" in item
                ? (item as FacebookPublish).channel_name
                : "",
          })
        );

      const otherPublishes: SocialPublish[] = currentMediaDetails.socials
        .filter((item) =>
          ["linkedin", "twitter", "instagram", "dailymotion"].includes(
            item.job_type
          )
        )
        .map((item) =>
          carryClientMarkers(item, {
            ...item,
            id: item.id,
            _channelId: destinationKey(item),
            published_id: item.id,
            routing_id: item.routing_id || item.published_id,
          })
        );

      // A still-local draft card (isNew === 100, never submitted to the
      // backend) is keyed by the destination's own channel id, but a real
      // backend record is keyed by its own unique record id - comparing raw
      // `id`s would never match, leaving the draft stuck on screen forever
      // even after the real record for that same destination exists (e.g.
      // right after a publish's create call actually succeeds), producing a
      // duplicate card for the same destination. Compare by destination
      // identity instead, same as `_channelId`/destinationKey above.
      //
      // A preset tab's draft (`_isPresetTabDraft` set) is the one exception:
      // it gets deliberately reset back to isNew:100 after each successful
      // publish (see handlePublished's preset-tab branch in
      // SocialPublish.tsx) so the same tab can take another "What to
      // publish" mode on that channel, even though a real record for that
      // channel now also exists. Retiring it here the same way a plain
      // draft is retired would immediately wipe out that reset on the very
      // next sync pass. `_presetId` is a DIFFERENT marker (only the
      // separate "Reuse Meta" expand-descriptor flow sets it) and doesn't
      // cover the toggle-based preset tab-group flow this exists for.
      const isKeptPresetDraft = (card: any) =>
        !!card?._isPresetTabDraft || !!card?._presetId;
      const existingUnpublishedYtCards = mediaChanged
        ? []
        : youtubePublishes.filter(
            (card) =>
              card.isNew === 100 &&
              (isKeptPresetDraft(card) ||
                !ytPublishes.some(
                  (newCard) => destinationKey(newCard) === card.id
                ))
          );
      const existingUnpublishedFbCards = mediaChanged
        ? []
        : facebookPublishes.filter(
            (card) =>
              card.isNew === 100 &&
              (isKeptPresetDraft(card) ||
                !fbPublishes.some(
                  (newCard) => destinationKey(newCard) === card.id
                ))
          );
      const existingUnpublishedOtherCards = mediaChanged
        ? []
        : socialPublishes.filter(
            (card) =>
              card.isNew === 100 &&
              (isKeptPresetDraft(card) ||
                !otherPublishes.some(
                  (newCard) => destinationKey(newCard) === card.id
                ))
          );

      // Build the render order from every record - one card per record, not
      // one per destination - plus any still-local, unsubmitted draft chips.
      const newPublishOrder = Array.from(
        new Set([
          ...existingUnpublishedYtCards.map((card) => card.id),
          ...existingUnpublishedFbCards.map((card) => card.id),
          ...existingUnpublishedOtherCards.map((card) => card.id),
          ...ytPublishes.map((item) => item.id),
          ...fbPublishes.map((item) => item.id),
          ...otherPublishes.map((item) => item.id),
        ])
      );
      setYoutubePublishes([...existingUnpublishedYtCards, ...ytPublishes]);
      setFacebookPublishes([...existingUnpublishedFbCards, ...fbPublishes]);
      setSocialPublishes([...existingUnpublishedOtherCards, ...otherPublishes]);
      setPublishOrder(newPublishOrder);
    } else if (mediaChanged) {
      // New media has no socials of its own - drop any leftover draft chips
      // from the previous media instead of leaving them displayed.
      setYoutubePublishes([]);
      setFacebookPublishes([]);
      setSocialPublishes([]);
      setPublishOrder([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMediaDetails?.id, currentMediaDetails?.socials, isPreview]);

  const addPublish = useCallback(
    (
      publish: YoutubePublish | FacebookPublish | SocialPublish,
      platform: string
    ) => {
      if (platform === "youtube") {
        setYoutubePublishes((prev) => [publish as YoutubePublish, ...prev]);
      } else if (platform === "facebook") {
        setFacebookPublishes((prev) => [publish as FacebookPublish, ...prev]);
      } else {
        setSocialPublishes((prev) => [publish as SocialPublish, ...prev]);
      }
      setPublishOrder((prev) => [publish.id, ...prev]);
    },
    []
  );

  const removePublish = useCallback((id: string, platform: string) => {
    if (platform === "youtube") {
      setYoutubePublishes((prevPublishes) =>
        prevPublishes.filter((publish) => publish?.id !== id)
      );
    } else if (platform === "facebook") {
      setFacebookPublishes((prevPublishes) =>
        prevPublishes.filter((publish) => publish?.id !== id)
      );
    } else {
      setSocialPublishes((prevPublishes) =>
        prevPublishes.filter((publish) => publish?.id !== id)
      );
    }
    setPublishOrder((prevOrder) =>
      prevOrder.filter((publishId) => publishId !== id)
    );
  }, []);

  return {
    youtubePublishes,
    facebookPublishes,
    socialPublishes,
    publishOrder,
    addPublish,
    removePublish,
    setYoutubePublishes,
    setFacebookPublishes,
    setSocialPublishes,
    setPublishOrder,
  };
};
